import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { linksService } from '@/services/links';
import type { 
  LinksState, 
  Link, 
  PaginationParams, 
  PaginationInfo,
  CreateLinkRequest,
  AdminCreateLinkRequest,
  UpdateLinkRequest 
} from '@/types';

export const useLinksStore = defineStore('links', () => {
  // State
  const links = ref<Link[]>([]);
  const currentLink = ref<Link | null>(null);
  const topLinks = ref<Link[]>([]);
  const pagination = ref<PaginationInfo>({
    limit: 50,
    cursor: undefined,
    hasMore: false,
  });
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Computed
  const linksState = computed<LinksState>(() => ({
    links: links.value,
    currentLink: currentLink.value,
    topLinks: topLinks.value,
    pagination: pagination.value,
    isLoading: isLoading.value,
    error: error.value,
  }));

  const totalClicks = computed(() => 
    links.value.reduce((sum, link) => sum + link.clickCount, 0)
  );

  const activeLinks = computed(() => 
    links.value.filter(link => link.isActive)
  );

  // Actions
  const clearError = () => {
    error.value = null;
  };

  const setError = (message: string) => {
    error.value = message;
  };

  const fetchLinks = async (params: PaginationParams = {}) => {
    try {
      isLoading.value = true;
      clearError();

      const response = await linksService.listLinks(params);
      links.value = response.data;
      pagination.value = {
        limit: params.limit || 50,
        cursor: response.cursor,
        hasMore: !!response.cursor,
      };
    } catch (err: any) {
      setError(err.message || 'Failed to fetch links');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTopLinks = async (limit: number = 10) => {
    try {
      clearError();
      const response = await linksService.getTopLinks(limit);
      topLinks.value = response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch top links');
      throw err;
    }
  };

  const fetchLink = async (shortCode: string) => {
    try {
      isLoading.value = true;
      clearError();

      const link = await linksService.getLink(shortCode);
      currentLink.value = link;
      return link;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch link');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const createLink = async (data: AdminCreateLinkRequest) => {
    try {
      isLoading.value = true;
      clearError();

      const newLink = await linksService.createLink(data);
      links.value.unshift(newLink);
      return newLink;
    } catch (err: any) {
      setError(err.message || 'Failed to create link');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateLink = async (shortCode: string, data: UpdateLinkRequest) => {
    try {
      isLoading.value = true;
      clearError();

      await linksService.updateLink(shortCode, data);
      
      // Convert string expiresAt to number timestamp for local state update
      const updatedData: Partial<Link> = {
        redirectURL: data.redirectURL,
        title: data.title,
        description: data.description,
        isActive: data.isActive,
      };
      if (data.expiresAt) {
        updatedData.expiresAt = new Date(data.expiresAt).getTime();
      }
      
      // Update in local state
      const index = links.value.findIndex(link => link.shortCode === shortCode);
      if (index !== -1) {
        links.value[index] = { ...links.value[index], ...updatedData };
      }
      
      if (currentLink.value?.shortCode === shortCode) {
        currentLink.value = { ...currentLink.value, ...updatedData };
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update link');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteLink = async (shortCode: string) => {
    try {
      isLoading.value = true;
      clearError();

      await linksService.deleteLink(shortCode);
      
      // Remove from local state
      links.value = links.value.filter(link => link.shortCode !== shortCode);
      topLinks.value = topLinks.value.filter(link => link.shortCode !== shortCode);
      
      if (currentLink.value?.shortCode === shortCode) {
        currentLink.value = null;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete link');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      const success = await linksService.copyToClipboard(text);
      if (!success) {
        throw new Error('Failed to copy to clipboard');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to copy to clipboard');
      throw err;
    }
  };

  const getFullShortUrl = (shortCode: string) => {
    return linksService.getFullShortUrl(shortCode);
  };

  const clearCurrentLink = () => {
    currentLink.value = null;
  };

  return {
    // State
    links,
    currentLink,
    topLinks,
    pagination,
    isLoading,
    error,
    
    // Computed
    linksState,
    totalClicks,
    activeLinks,
    
    // Actions
    clearError,
    setError,
    fetchLinks,
    fetchTopLinks,
    fetchLink,
    createLink,
    updateLink,
    deleteLink,
    copyToClipboard,
    getFullShortUrl,
    clearCurrentLink,
  };
});
